import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
// auth
import { AuthGuard } from 'src/auth/guard';
// layouts
import MardamLayout from 'src/layouts/mardam';
// components
import { LoadingScreen } from 'src/components/loading-screen';

// ----------------------------------------------------------------------

// OVERVIEW
const IndexPage = lazy(() => import('src/pages/mardam-dashboard/app'));
const TenantsListPage = lazy(() => import('src/pages/mardam-dashboard/users/list'));
const TenantCreatePage = lazy(() => import('src/pages/mardam-dashboard/users/new'));
const CityListPage = lazy(() =>
  import('src/pages/mardam-dashboard/cities_Municipalities/cityList')
);
const ViolationListPage = lazy(() => import('src/pages/mardam-dashboard/violations/violationList'));
const CityAddPage = lazy(() => import('src/pages/mardam-dashboard/cities_Municipalities/addCity'));

const MunicipalityListPage = lazy(() =>
  import('src/pages/mardam-dashboard/cities_Municipalities/municipalityList')
);
const MunicipalityAddPage = lazy(() =>
  import('src/pages/mardam-dashboard/cities_Municipalities/addMunicipality')
);

const RegionListPage = lazy(() => import('src/pages/mardam-dashboard/regions/list'));
const RegionCreatePage = lazy(() => import('src/pages/mardam-dashboard/regions/new'));

const GarbageUnloadingPage = lazy(() =>
  import('src/pages/mardam-dashboard/garbage-unloading/list')
);

const GarbageReport = lazy(() => import('src/pages/mardam-dashboard/garbage-unloading/report'));

const PickupListPage = lazy(() => import('src/pages/mardam-dashboard/pickup-points/list'));
const PickupCreatePage = lazy(() => import('src/pages/mardam-dashboard/pickup-points/new'));

const SupervisorsListPage = lazy(() => import('src/pages/mardam-dashboard/supervisors/list'));
const SupervisorsCreatePage = lazy(() => import('src/pages/mardam-dashboard/supervisors/new'));

const DriverListPage = lazy(() => import('src/pages/mardam-dashboard/drivers/list'));
const DriverCreatePage = lazy(() => import('src/pages/mardam-dashboard/drivers/new'));

const ContractorsListPage = lazy(() => import('src/pages/mardam-dashboard/contractors/list'));
const ContractorVehiclesListPage = lazy(() =>
  import('src/pages/mardam-dashboard/contractors/listVehicles')
);
const ContractorsCreatePage = lazy(() => import('src/pages/mardam-dashboard/contractors/new'));
const VehiclesListPage = lazy(() => import('src/pages/mardam-dashboard/vehicles/list'));
const VehiclesCreatePage = lazy(() => import('src/pages/mardam-dashboard/vehicles/new'));

const SettingsPage = lazy(() => import('src/pages/mardam-dashboard/configuration/account'));

const ScalePage = lazy(() => import('src/pages/mardam-dashboard/scale'));
const ScaleAddPage = lazy(() => import('src/pages/mardam-dashboard/scale/addScale'));
const ScaleEditPage = lazy(() => import('src/pages/mardam-dashboard/scale/editScale'));

const HardwarePage = lazy(() => import('src/pages/mardam-dashboard/hardware'));
const HardwareAddPage = lazy(() => import('src/pages/mardam-dashboard/hardware/addHardware'));
const HardwareEditPage = lazy(() => import('src/pages/mardam-dashboard/hardware/editHardware'));

const ZonesPage = lazy(() => import('src/pages/mardam-dashboard/zones'));
const ZonesAddPage = lazy(() => import('src/pages/mardam-dashboard/zones/addZones'));
const ZonesEditPage = lazy(() => import('src/pages/mardam-dashboard/zones/editZones'));

const GeographicalLocationsPage = lazy(() =>
  import('src/pages/mardam-dashboard/geographical-locations')
);
const GeographicalLocationsAddPage = lazy(() =>
  import('src/pages/mardam-dashboard/geographical-locations/addGeographicalLocations')
);
const GeographicalLocationsEditPage = lazy(() =>
  import('src/pages/mardam-dashboard/geographical-locations/editGeographicalLocations')
);

const TypesOfWastesPage = lazy(() => import('src/pages/mardam-dashboard/types-of-wastes'));
const TypesOfWastesAddPage = lazy(() =>
  import('src/pages/mardam-dashboard/types-of-wastes/addTypeOfWastes')
);
const TypesOfWastesEditPage = lazy(() =>
  import('src/pages/mardam-dashboard/types-of-wastes/editTypeOfWastes')
);

const TypesOfVehiclesPage = lazy(() => import('src/pages/mardam-dashboard/types-of-vehicles'));
const TypesOfVehiclesAddPage = lazy(() =>
  import('src/pages/mardam-dashboard/types-of-vehicles/addTypeOfVehicles')
);
const TypesOfVehiclesEditPage = lazy(() =>
  import('src/pages/mardam-dashboard/types-of-vehicles/editTypeOfVehicles')
);

const ContractsPage = lazy(() => import('src/pages/mardam-dashboard/contracts'));
const ContractsAddPage = lazy(() => import('src/pages/mardam-dashboard/contracts/addContracts'));

const EntryPermitsPage = lazy(() => import('src/pages/mardam-dashboard/entry-permits'));
const EntryPermitsAddPage = lazy(() =>
  import('src/pages/mardam-dashboard/entry-permits/addEntryPermits')
);
const EntryPermitsEditPage = lazy(() =>
  import('src/pages/mardam-dashboard/entry-permits/editEntryPermits')
);

const PermitsPage = lazy(() => import('src/pages/mardam-dashboard/permits'));
const PermitsAddPage = lazy(() => import('src/pages/mardam-dashboard/permits/addPermits'));

// TEST RENDER PAGE BY ROLE
const PermissionDeniedPage = lazy(() => import('src/pages/dashboard/permission'));
// BLANK PAGE
const BlankPage = lazy(() => import('src/pages/dashboard/blank'));

// ----------------------------------------------------------------------

export const mardamRoutes = [
  {
    path: 'mardam',
    element: (
      <AuthGuard>
        <MardamLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </MardamLayout>
      </AuthGuard>
    ),
    children: [
      { element: <IndexPage />, index: true },
      { path: 'tenants', element: <TenantsListPage /> },
      { path: 'cities-municipalities', element: <CityListPage /> },
      { path: 'cities-municipalities/new', element: <CityAddPage /> },
      { path: 'municipalities/view/:id', element: <MunicipalityListPage /> },
      { path: 'municipalities/new/:id', element: <MunicipalityAddPage /> },
      { path: 'tenants/new', element: <TenantCreatePage /> },
      { path: 'regions', element: <RegionListPage /> },
      { path: 'garbage-unloading', element: <GarbageUnloadingPage /> },
      { path: 'garbage-report', element: <GarbageReport /> },
      { path: 'regions/new', element: <RegionCreatePage /> },
      { path: 'pickup-points', element: <PickupListPage /> },
      { path: 'pickup-points/new', element: <PickupCreatePage /> },
      { path: 'violations', element: <ViolationListPage /> },
      { path: 'supervisors', element: <SupervisorsListPage /> },
      { path: 'supervisors/new', element: <SupervisorsCreatePage /> },
      { path: 'drivers', element: <DriverListPage /> },
      { path: 'drivers/new', element: <DriverCreatePage /> },
      { path: 'contractors', element: <ContractorsListPage /> },
      { path: 'contractors/vehicles/:id', element: <ContractorVehiclesListPage /> },
      { path: 'contractors/new', element: <ContractorsCreatePage /> },
      { path: 'vehicles', element: <VehiclesListPage /> },
      { path: 'vehicles/new', element: <VehiclesCreatePage /> },
      { path: 'settings', element: <SettingsPage /> },
      { path: 'scale', element: <ScalePage /> },
      { path: 'scale/add', element: <ScaleAddPage /> },
      { path: 'scale/edit', element: <ScaleEditPage /> },
      { path: 'hardware', element: <HardwarePage /> },
      { path: 'hardware/add', element: <HardwareAddPage /> },
      { path: 'hardware/edit', element: <HardwareEditPage /> },
      { path: 'zones', element: <ZonesPage /> },
      { path: 'zones/add', element: <ZonesAddPage /> },
      { path: 'zones/edit', element: <ZonesEditPage /> },
      { path: 'landfill-areas', element: <GeographicalLocationsPage /> },
      { path: 'landfill-areas/add', element: <GeographicalLocationsAddPage /> },
      { path: 'landfill-areas/edit', element: <GeographicalLocationsEditPage /> },
      { path: 'types-of-wastes', element: <TypesOfWastesPage /> },
      { path: 'types-of-wastes/add', element: <TypesOfWastesAddPage /> },
      { path: 'types-of-wastes/edit', element: <TypesOfWastesEditPage /> },
      { path: 'types-of-vehicles', element: <TypesOfVehiclesPage /> },
      { path: 'types-of-vehicles/add', element: <TypesOfVehiclesAddPage /> },
      { path: 'types-of-vehicles/edit', element: <TypesOfVehiclesEditPage /> },
      { path: 'contracts', element: <ContractsPage /> },
      { path: 'contracts/add', element: <ContractsAddPage /> },
      { path: 'entry-permits', element: <EntryPermitsPage /> },
      { path: 'entry-permits/add', element: <EntryPermitsAddPage /> },
      { path: 'entry-permits/edit', element: <EntryPermitsEditPage /> },
      { path: 'permits', element: <PermitsPage /> },
      { path: 'permits/add', element: <PermitsAddPage /> },
      // { path: 'permits/edit', element: <EntryPermitsEditPage />},
      { path: 'permission', element: <PermissionDeniedPage /> },
      { path: 'blank', element: <BlankPage /> },
    ],
  },
];
